/**
 * Axios API client.
 *
 * Requests always go through — no preemptive blocking.
 * Token refresh on 401, network error reporting to health engine.
 */
import axios from 'axios'
import { reportNetworkFailure } from '@/composables/useNetworkHealth'
import {
  getAccessToken,
  refreshAccessToken,
  awaitBootGate,
  clearAccessToken,
  clearRefreshToken,
} from '@/auth/tokenManager'
import { isNative } from '@/native/platform'
import { isTechPreview, notifyParent } from '@/preview/techPreview'

let globalAbortController = new AbortController()

export function abortAllRequests() {
  globalAbortController.abort()
  globalAbortController = new AbortController()
  console.log('[API] All pending requests aborted')
}

const AUTH_KEYS = [
  'access_token', 'refresh_token', 'user', 'domain', 'permissions',
  'is_technician_session', 'interface_version_id', 'activity_session_id',
  // Anti-FOUC mirror of the account's ui_preferences (see App.vue).
  'ui_prefs_v1',
]

function clearAuthStorage() {
  AUTH_KEYS.forEach(k => {
    try { localStorage.removeItem(k) } catch { /* storage unavailable */ }
    try { sessionStorage.removeItem(k) } catch { /* storage unavailable */ }
  })
}

// Absolute API origin for native (Capacitor) builds. On web this is unset, so
// we keep the same-origin relative '/api/v1' path (Caddy proxies it, the
// Service Worker rules + cookies all assume same-origin). When VITE_API_BASE_URL
// is provided (`.env.mobile`), every request targets that absolute origin
// because the native shell runs on capacitor://localhost / http://localhost and
// a relative path would resolve against the WebView origin, not the server.
const API_BASE = (import.meta.env?.VITE_API_BASE_URL || '').replace(/\/+$/, '')
const API_BASE_URL = API_BASE ? `${API_BASE}/api/v1` : '/api/v1'

/** Public auth endpoints must never carry a stale Bearer (native Keychain persists tokens). */
const PUBLIC_AUTH_PATHS = ['/auth/login/', '/auth/technician-login/', '/auth/register/']

function isPublicAuthRequest(config) {
  const url = config?.url || ''
  return PUBLIC_AUTH_PATHS.some((p) => url.includes(p))
}

/**
 * App routes that render WITHOUT a session — customer-facing links opened
 * straight from an SMS/email (estimate `/e/`, hosted pay `/pay/`, e-sign
 * `/sign/`), the public online-booking wizard (`/book/` — reached from the
 * company website or its Google Business Profile "Book online" link) and the
 * password/legal pages. A stray background 401 (e.g. the technician
 * offline-media snapshot) must NEVER bounce an anonymous visitor on these
 * pages to `/login`: the link has to open as a standalone page, the same way
 * the customer estimate/pay links already do. Mirrors the existing `/login`
 * exemption below.
 */
const PUBLIC_APP_PATH_PREFIXES = [
  '/login', '/sign/', '/e/', '/pay/', '/book/',
  '/forgot-password', '/reset-password', '/accept-company-invite',
  // Public legal pages (Google OAuth / Play verification, anonymous visitors):
  // they must open standalone without a session. A background 401 here must not
  // hard-redirect to /login. Keep in sync with the router's public legal routes
  // and TECH_SESSION_PUBLIC_PATHS.
  '/privacy', '/terms', '/cookies', '/delete-account',
  '/impersonate',
]

function isPublicAppPath(pathname) {
  return PUBLIC_APP_PATH_PREFIXES.some((p) => pathname.startsWith(p))
}

const apiClient = axios.create({
  baseURL: API_BASE_URL,
  // Hard ceiling so a request can never hang forever on a flaky mobile
  // connection (previously requests with no timeout left the tech UI spinner
  // stuck, prompting techs to tap the same action again → duplicates).
  // 60s is generous enough for heavy reports/sync endpoints.
  timeout: 60000,
  // Send cookies so the httpOnly refresh cookie (dual/strict AUTH_MODE) is used.
  // Same-origin already sends cookies, but this future-proofs an auth subdomain
  // and is harmless in legacy mode (no auth cookie set there).
  withCredentials: true,
  headers: { 'Content-Type': 'application/json' }
})

// NOTE: No offline gate. Requests go through even when health ping fails.
// Token refresh, heartbeat, and register-device MUST work regardless.
// If server is truly down, requests fail naturally with network errors.

// AUTH: Add token + attach global abort signal for cleanup on logout.
// On boot we first await the boot gate (the silent refresh) so the very first
// requests carry a fresh in-memory token instead of flashing transient 401s when
// the 15-min access cookie has expired. The gate is instant once settled, and the
// boot refresh itself uses the raw axios instance (no interceptor) → no deadlock.
apiClient.interceptors.request.use(async config => {
  // Never wait on boot refresh for public login/register — avoids racing a stale
  // refresh attempt against an in-flight password login on the native app.
  if (!isPublicAuthRequest(config)) {
    try { await awaitBootGate() } catch (_) { /* never block on boot refresh errors */ }
  }
  const token = getAccessToken()
  if (token && !isPublicAuthRequest(config)) {
    config.headers.Authorization = `Bearer ${token}`
  }
  // Preview iframe: mark every request so the backend NEVER falls back to the
  // admin's httpOnly cookie (same-origin iframes always send it) — an expired
  // preview token must fail, not silently become an admin session.
  if (isTechPreview()) {
    config.headers['X-Tech-Preview'] = '1'
  }
  if (!config.signal) {
    config.signal = globalAbortController.signal
  }
  return config
})

// v3.3 (May 8) — invalidate the workbox api-cache after every successful
// write request. Without this, a freshly-saved record can be hidden by an
// old GET that the Service Worker decides to serve from cache. This wipes
// the entire api-cache (it's small; max 50 entries by config), forcing the
// next GET to go to the network.
apiClient.interceptors.response.use(
  response => {
    try {
      const m = (response.config?.method || '').toLowerCase()
      if (m === 'post' || m === 'put' || m === 'patch' || m === 'delete') {
        if (typeof caches !== 'undefined' && caches?.delete) {
          caches.delete('api-cache').catch(() => {})
        }
      }
    } catch (_) { /* never block the response */ }
    return response
  },
  error => Promise.reject(error),
)

// RESPONSE: Detect network failures → instantly trigger offline
const SEAT_LIMIT_CODE = 'seat_limit_reached'
export const LOGIN_NOTICE_KEY = 'fsj_login_notice'

function endSessionForSeatLimit(message) {
  console.warn('[Auth] Seat limit reached — ending the session', message)
  try { sessionStorage.setItem(LOGIN_NOTICE_KEY, message || 'All user seats of your company are in use right now.') } catch (_) { /* ignore */ }
  clearAccessToken()
  clearRefreshToken()
  clearAuthStorage()
  if (!window.location.pathname.startsWith('/login')) {
    window.location.href = '/login'
  }
}

const PLAN_LIMIT_CODES = ['trial_ended', 'plan_pool_exhausted']
let lastPlanLimitNoticeAt = 0

async function notifyPlanLimit(data) {
  console.warn('[Billing] Plan limit refused the request', data)
  const now = Date.now()
  if (now - lastPlanLimitNoticeAt < 15000) return
  lastPlanLimitNoticeAt = now
  try {
    const { useNotify } = await import('@/composables/useNotify')
    useNotify().notify.warning(data?.error || 'Your subscription plan does not allow this action.')
  } catch (e) {
    console.warn('[Billing] Could not show the plan limit notice:', e?.message || e)
  }
}

apiClient.interceptors.response.use(
  response => response,
  async error => {
    // Canceled requests (e.g., during logout) — silently reject, no logs
    if (axios.isCancel(error) || error?.code === 'ERR_CANCELED') {
      return Promise.reject(error)
    }

    // Silently pass through offline rejections
    if (error?.__offline) return Promise.reject(error)

    // Network error or 502/503 → tell health engine immediately
    if (
      error?.code === 'ERR_NETWORK' ||
      error?.message === 'Network Error' ||
      error?.response?.status === 502 ||
      error?.response?.status === 503
    ) {
      reportNetworkFailure()
      return Promise.reject(error)
    }

    // 402 Payment Required → atomic billing rejected the request (insufficient balance)
    // Show InsufficientBalanceDialog. Caller can also handle it directly.
    if (error.response?.status === 402 && error.response?.data?.error === 'insufficient_balance') {
      try {
        // Lazy-load to avoid circular import (store imports apiClient)
        const { useBillingStore } = await import('@/stores/billingStore')
        const store = useBillingStore()
        store.setInsufficient(error.response.data)
        // Refresh balance silently in background
        store.fetchBalance({ force: true }).catch(() => {})
      } catch (e) {
        console.warn('[Auth] Could not open InsufficientBalanceDialog:', e?.message || e)
      }
      return Promise.reject(error)
    }

    // 403 seat_limit_reached → all concurrent seats of the company contract are
    // taken. The session cannot continue: back to sign-in with the reason shown.
    if (error.response?.status === 403 && error.response?.data?.code === SEAT_LIMIT_CODE) {
      endSessionForSeatLimit(error.response.data.error)
      return Promise.reject(error)
    }

    // 403 trial_ended / plan_pool_exhausted → the subscription plan stopped this
    // action (free trial over = read-only, or the package is used up).
    if (error.response?.status === 403 && PLAN_LIMIT_CODES.includes(error.response?.data?.code)) {
      notifyPlanLimit(error.response.data)
      return Promise.reject(error)
    }

    // 401 → refresh token (God Mode sessions don't have refresh tokens — just redirect)
    const originalRequest = error.config
    if (error.response?.status === 401 && !originalRequest._retry) {
      // Tech App Preview iframe: never refresh (that would ride the admin's
      // cookie) — tell the parent page so it can offer a session restart.
      if (isTechPreview()) {
        notifyParent('tech_preview_unauthorized')
        return Promise.reject(error)
      }
      // Wrong password on login is 401 — do not run refresh/retry (device had stale tokens).
      if (isPublicAuthRequest(originalRequest)) {
        return Promise.reject(error)
      }
      // On the login screen (or any PUBLIC page opened without a session, e.g.
      // the /sign e-sign link and the customer /e/ estimate & /pay pages),
      // zombie background requests (entitlements, profile, offline-media
      // snapshot) must not trigger refresh→logout→redirect. The visitor is
      // anonymous by design and the page must stay put.
      try {
        if (isPublicAppPath(window.location.pathname)) {
          return Promise.reject(error)
        }
      } catch (_) { /* ignore */ }
      originalRequest._retry = true
      const isGodMode = !!sessionStorage.getItem('god_mode')
      if (isGodMode) {
        console.warn('[Auth] 401 in God Mode — closing tab')
        sessionStorage.clear()
        window.close()
        return Promise.reject(error)
      }
      // Always attempt a refresh (non-God-Mode): in legacy/dual a body refresh
      // token may exist in storage; in dual/strict the httpOnly cookie is sent
      // automatically (withCredentials) and is the ONLY credential after token
      // removal (Phase 4.6). Single-flight: concurrent 401s coalesce into one.
      try {
        console.log('[Auth] 401 received, attempting token refresh...')
        const access_token = await refreshAccessToken(axios)
        if (access_token) {
          originalRequest.headers.Authorization = `Bearer ${access_token}`
          console.log('[Auth] Token refreshed successfully, retrying request')
          return apiClient(originalRequest)
        }
        console.warn('[Auth] Refresh returned no access token — not retrying')
        return Promise.reject(error)
      } catch (refreshErr) {
        if (refreshErr?.response?.data?.code === SEAT_LIMIT_CODE) {
          endSessionForSeatLimit(refreshErr.response.data.error)
          return Promise.reject(error)
        }
        const refreshStatus = refreshErr?.response?.status
        // TICK-AUTH-0607: 400 = "Refresh token required" — the app has NO
        // refresh credential at all (neither body token nor cookie). This is a
        // DEAD session: retrying can never succeed, and keeping it produced
        // zombie sessions where every tap failed with a generic error while
        // the user was never sent back to the login screen.
        const isFatalRefresh = refreshStatus === 400 || refreshStatus === 401 || refreshStatus === 403
        if (isFatalRefresh) {
          // Do NOT hard-logout on a SINGLE failed refresh. After a 15-min token
          // expiry a burst of parallel 401s (REST + WS reconnects on call pickup)
          // can race the refresh and one may transiently fail. Retry ONCE after a
          // short delay — a concurrent successful refresh may already have produced
          // a fresh token. Only log out if the refresh is STILL rejected.
          try {
            await new Promise(r => setTimeout(r, 1000))
            const retryToken = await refreshAccessToken(axios)
            if (retryToken) {
              originalRequest.headers.Authorization = `Bearer ${retryToken}`
              console.log('[Auth] Token refreshed on retry, retrying request')
              return apiClient(originalRequest)
            }
          } catch (retryErr) {
            const retryStatus = retryErr?.response?.status
            if (retryStatus !== 400 && retryStatus !== 401 && retryStatus !== 403) {
              console.warn('[Auth] Retry refresh failed (network/server) — keeping session')
              return Promise.reject(error)
            }
          }
          console.warn('[Auth] Refresh rejected twice (', refreshStatus, ') — logging out')
          try { error.__sessionExpired = true } catch (_) { /* ignore */ }
          clearAccessToken()
          let keepBiometricVault = false
          if (isNative()) {
            try {
              const { getBiometricConfiguredPhone } = await import('@/native/biometricEnrollment')
              keepBiometricVault = !!getBiometricConfiguredPhone()
            } catch { /* ignore */ }
          }
          if (!keepBiometricVault) {
            clearRefreshToken()
            clearAuthStorage()
          }
          // Reload-loop guard: background requests (global widgets) fire on the
          // login page too; redirecting when we are ALREADY on /login reloads
          // the page in an endless loop and the user can never type credentials.
          if (!window.location.pathname.startsWith('/login')) {
            window.location.href = '/login'
          }
        } else {
          console.warn('[Auth] Refresh failed (network/server error:', refreshErr?.message, ') — NOT logging out, keeping session')
        }
        return Promise.reject(error)
      }
    }
    return Promise.reject(error)
  }
)

/**
 * Check if error is an offline/network error that should be silently ignored.
 * Use in component catch blocks: if (isOfflineError(err)) return
 */
export function isOfflineError(err) {
  return err?.__offline || err?.code === 'ERR_NETWORK' || err?.message === 'Network Error' || err?.code === 'ERR_CANCELED'
}

/**
 * Client-side request timeout (axios ECONNABORTED). On flaky mobile uplinks a
 * large media upload can exceed the timeout even though the connection is fine.
 * Such errors must be treated as "retry later" (queue), not as a hard failure
 * that loses the file.
 */
export function isTimeoutError(err) {
  return err?.code === 'ECONNABORTED' ||
    (typeof err?.message === 'string' && err.message.toLowerCase().includes('timeout'))
}

export { AUTH_KEYS, clearAuthStorage }

export function useApi() {
  return apiClient
}

export default apiClient
